import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';

const BRAND = '#3B1C73';
const BRAND_HOVER = '#2c1457';

const FAQS = [
  {
    q: 'What is Physign?',
    a: 'Physign is a healthcare platform that helps patients book verified doctors, nurses, physiotherapists, and home healthcare services conveniently.',
  },
  {
    q: 'How does Physign work?',
    a: 'Users can search healthcare professionals, choose a suitable service, select a time slot, and book an appointment online.',
  },
  {
    q: 'Can I book a doctor for a home visit?',
    a: 'Yes, Physign allows patients to book verified doctors for home visits based on availability and location.',
  },
  {
    q: 'Can I book a nurse at home?',
    a: 'Yes, you can book trained nurses for injections, wound dressing, elderly care, post-surgery care, and other nursing services.',
  },
  {
    q: 'Are the doctors on Physign verified?',
    a: 'Yes, all doctors undergo a verification process before being listed on the platform.',
  },
  {
    q: 'How can I find a doctor near me?',
    a: 'Simply enter your location and browse available doctors based on specialty, experience, and consultation fees.',
  },
  {
    q: 'What medical specialties are available on Physign?',
    a: 'Physign offers access to General Physicians, Pediatricians, Gynecologists, Dermatologists, Orthopedicians, Physiotherapists, and more.',
  },
  {
    q: 'Can I book a same-day doctor appointment?',
    a: "Yes, same-day appointments may be available depending on the doctor's schedule.",
  },
  {
    q: 'How much does a doctor consultation cost?',
    a: "Consultation fees vary depending on the doctor's specialty, experience, and service type.",
  },
  {
    q: 'Can I reschedule or cancel my appointment?',
    a: "Yes, appointments can be rescheduled or canceled according to the platform's cancellation policy.",
  },
  {
    q: 'What is home healthcare?',
    a: "Home healthcare includes medical services provided at a patient's home, such as doctor visits, nursing care, physiotherapy, and health monitoring.",
  },
  {
    q: 'Can I book a physiotherapist through Physign?',
    a: 'Yes, Physign allows patients to book physiotherapists for home visits and clinic consultations.',
  },
  {
    q: 'Is Physign available 24/7?',
    a: 'Appointment booking is available online at any time, while healthcare provider availability may vary.',
  },
  {
    q: 'Is my personal information secure?',
    a: 'Yes, Physign follows security measures to protect patient information and privacy.',
  },
  {
    q: 'How do I pay for my appointment?',
    a: 'Payments can be made through the available online payment methods or as specified during booking.',
  },
  {
    q: 'Can elderly patients use Physign?',
    a: 'Yes, Physign is designed to make healthcare access easier for senior citizens and their families.',
  },
  {
    q: 'How quickly can I get a doctor at home?',
    a: 'Availability depends on your location and provider schedule. In selected areas, home visits may be available within a short time.',
  },
  {
    q: 'Why should I choose Physign?',
    a: 'Physign offers verified healthcare professionals, easy booking, transparent pricing, and convenient home healthcare services.',
  },
  {
    q: 'Can clinics and doctors join Physign?',
    a: 'Yes, healthcare providers can register and create professional profiles on the platform.',
  },
  {
    q: 'How do I contact Physign support?',
    a: 'You can contact the Physign support team through the website, email, or customer support channels.',
  },
];

// FAQ schema for Google rich snippets
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

function FAQItem({ index, q, a, open, onToggle }) {
  return (
    <div
      className="border border-slate-200 rounded-xl overflow-hidden bg-white transition-shadow hover:shadow-sm"
      data-testid={`faq-item-${index}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        data-testid={`faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900 text-base md:text-[15px]">
          {index + 1}. {q}
        </span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ color: BRAND }}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-slate-600 leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0); // first open by default
  const [query, setQuery] = useState('');

  const filtered = query
    ? FAQS.filter(
        (f) =>
          f.q.toLowerCase().includes(query.toLowerCase()) ||
          f.a.toLowerCase().includes(query.toLowerCase())
      )
    : FAQS;

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <SEO
        path="/faq"
        title="Frequently Asked Questions"
        description="Find answers to common questions about Physign - doctor bookings, home visits, nurse services, verified providers, payments, cancellations, and more."
        schema={FAQ_SCHEMA}
      />

      {/* Header */}
      <header className="border-b border-slate-100 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-logo-link">
            <span
              className="text-xl font-bold"
              style={{ color: BRAND, fontFamily: 'Plus Jakarta Sans' }}
            >
              Physign
            </span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/" className="text-slate-600 hover:opacity-80" style={{ ['--hov']: BRAND }}>Home</Link>
            <Link to="/about" className="text-slate-600 hover:opacity-80">About</Link>
            <Link
              to="/auth"
              className="text-white px-4 py-2 rounded-full font-medium transition-colors"
              style={{ background: BRAND }}
              onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_HOVER)}
              onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
            >
              Login / Sign Up
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        {/* Heading */}
        <div className="mb-8 text-center">
          <span
            className="font-semibold uppercase tracking-wide text-sm"
            style={{ color: BRAND }}
          >
            Help Center
          </span>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
            Frequently Asked Questions
          </h1>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            Everything you need to know about booking doctors, nurses, and
            healthcare services on Physign.
          </p>
        </div>

        {/* Search */}
        <div className="mb-8">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a question (e.g. home visit, payment, doctor)"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2"
            style={{ ['--tw-ring-color']: BRAND }}
            data-testid="faq-search"
          />
          {query && (
            <p className="text-xs text-slate-500 mt-2">
              {filtered.length} {filtered.length === 1 ? 'result' : 'results'} for &quot;{query}&quot;
            </p>
          )}
        </div>

        {/* FAQs */}
        <div className="space-y-3" data-testid="faq-list">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>No questions matched. Try a different keyword.</p>
            </div>
          ) : (
            filtered.map((f, i) => (
              <FAQItem
                key={f.q}
                index={FAQS.indexOf(f)}
                q={f.q}
                a={f.a}
                open={openIdx === FAQS.indexOf(f)}
                onToggle={() => setOpenIdx(openIdx === FAQS.indexOf(f) ? -1 : FAQS.indexOf(f))}
              />
            ))
          )}
        </div>

        {/* Contact CTA */}
        <div
          className="mt-12 rounded-2xl p-8 text-center text-white"
          style={{ background: BRAND }}
        >
          <h2 className="text-2xl font-bold mb-2">Still have questions?</h2>
          <p className="opacity-90 mb-5">
            Our support team is happy to help you with bookings and any concerns.
          </p>
          <a
            href="mailto:info@physign.com"
            className="inline-block bg-white px-6 py-3 rounded-full font-semibold transition-colors hover:bg-slate-100"
            style={{ color: BRAND }}
            data-testid="faq-contact-cta"
          >
            Email info@physign.com
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <p>© 2026 Physign Technologies Pvt. Ltd. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/about" className="hover:opacity-80">About</Link>
            <Link to="/faq" className="hover:opacity-80">FAQ</Link>
            <Link to="/terms" className="hover:opacity-80">Terms</Link>
            <Link to="/privacy-policy" className="hover:opacity-80">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
